import type { ScreenId } from '@/generated/screens.g'
import type { NoteAttachment } from '@/types/note'
import type { PlanGateStage } from '@/types/plan'

export const INBOX_FOLDERS = ['inbox', 'sent'] as const
export type InboxFolder = (typeof INBOX_FOLDERS)[number]

/** The three mailbox tabs: Inbox and Sent are folders; Archive holds archived threads of any folder. */
export const INBOX_TABS = ['inbox', 'sent', 'archive'] as const
export type InboxTab = (typeof INBOX_TABS)[number]

export const INBOX_SUBJECT_TYPES = [
  'post',
  'article-run',
  'plan-run',
  'account',
  'design-run',
  'run',
  'note',
] as const
export type InboxSubjectType = (typeof INBOX_SUBJECT_TYPES)[number]

/** The record a thread is about. */
export interface InboxSubject {
  type: InboxSubjectType
  id: string
}

export const INBOX_SENDERS = ['raldo', 'you', 'linkedin'] as const
export type InboxSender = (typeof INBOX_SENDERS)[number]

export const INBOX_TARGET_TABS = ['research', 'draft', 'visibility'] as const
export type InboxTargetTab = (typeof INBOX_TARGET_TABS)[number]

/** The screen a message opens, with the record and tab to show on it. */
export interface InboxTarget {
  screen: ScreenId
  item?: string
  tab?: InboxTargetTab
}

export const RESEARCH_RUN_KINDS = [
  'brandFacts',
  'customerProfile',
  'contentStrategy',
  'competitors',
  'visibility',
  'aiAnswers',
  'documents',
] as const
export type ResearchRunKind = (typeof RESEARCH_RUN_KINDS)[number]

export type RunOutcome = 'completed' | 'partial'

export interface PlanProposalSummary {
  topicsChanged: number
  pagesAdded: number
  pagesRemoved: number
  pagesChanged: number
}

/** A post's first-week figures; `engagementRate` is a 0-1 fraction. */
export interface PostResultsParams {
  title: string
  impressions: number
  reach: number
  clicks: number
  engagementRate: number
}

/** What a person did with a finished brand style. */
export type BrandStyleOutcome = 'used' | 'discarded'

export type NoteOrigin = 'app' | 'telegram'

/** The kind of a message and the typed params its row and preview are built from; no display text. */
export type InboxEntryContent =
  | { kind: 'article.questions'; params: { pageTitle: string; questionCount: number } }
  | { kind: 'article.ready'; params: { pageTitle: string; outcome: RunOutcome } }
  | { kind: 'plan.proposal'; params: PlanProposalSummary }
  | { kind: 'plan.topics'; params: { topics: number } }
  | { kind: 'plan.reminder'; params: { stage: PlanGateStage } }
  | { kind: 'plan.ready'; params: { outcome: RunOutcome } }
  | { kind: 'post.draft'; params: { score: number | null; title: string; hook: string } }
  | { kind: 'post.publishFailed'; params: { errorCode: string; title: string } }
  | { kind: 'post.published'; params: { title: string } }
  | { kind: 'post.results'; params: PostResultsParams }
  | { kind: 'run.failed'; params: { runKind: string; errorCode: string | null } }
  | { kind: 'account.disconnected'; params: { platform: string; errorCode: string } }
  | { kind: 'research.ready'; params: { runKind: ResearchRunKind } }
  | { kind: 'design.ready'; params: { partial: boolean } }
  | { kind: 'brandStyle.ready'; params: { designName: string } }
  | { kind: 'brandStyle.decided'; params: { decision: BrandStyleOutcome } }
  | {
      kind: 'note'
      params: Record<string, never>
      body: string
      attachments: NoteAttachment[]
      origin: NoteOrigin
      authorUid: string
    }

export type InboxEntryKind = InboxEntryContent['kind']

export const INBOX_ENTRY_KINDS: readonly InboxEntryKind[] = [
  'article.questions',
  'article.ready',
  'plan.proposal',
  'plan.topics',
  'plan.reminder',
  'plan.ready',
  'post.draft',
  'post.publishFailed',
  'post.published',
  'post.results',
  'run.failed',
  'account.disconnected',
  'research.ready',
  'design.ready',
  'brandStyle.ready',
  'brandStyle.decided',
  'note',
]

interface InboxEntryBase {
  id: string
  sender: InboxSender
  target: InboxTarget | null
  createdAt: Date
}

export type InboxEntry = InboxEntryBase & InboxEntryContent

/** The newest message of a thread in brief: its kind and typed params, its sender and, for a note, the start of its text. */
export type InboxLatest = {
  [K in InboxEntryKind]: {
    kind: K
    params: Extract<InboxEntryContent, { kind: K }>['params']
  }
}[InboxEntryKind] & { sender: InboxSender; snippet: string | null }

/** One conversation-like group of messages about one subject. */
export interface InboxThread {
  id: string
  subject: InboxSubject
  folder: InboxFolder
  actionNeeded: boolean
  archivedAt: Date | null
  lastEntryAt: Date
  createdAt: Date
  /** The newest message in brief: its kind decides how the thread looks. */
  latest: InboxLatest
}

/** When one member last read each thread, by thread id. */
export type InboxReadMarks = ReadonlyMap<string, Date>
